import { useCallback, useEffect, useState } from 'react';
import DeployModal from '~/components/ui/DeployModal';
import { DownloadModal } from '~/components/ui/DownloadModal';
import { workbenchStore } from '~/lib/stores/workbench';
import * as Sentry from '@sentry/remix';

// import { IssueDropdown } from '~/components/ui/IssueDropdown';
import HelpMenu from '~/components/ui/HelpMenu';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { getFramework } from '~/lib/persistence/db';
import { chatId } from '~/lib/persistence';
import { db } from '~/lib/persistence';
import { ProUpgradeModal } from '~/components/ui/ProUpgradeModal';
import { userStore } from '~/lib/stores/user';

// Add array of frameworks where deploy should be disabled
const DEPLOY_DISABLED_FRAMEWORKS = ['angular_css', 'reactnative', 'flutter'];

const FRAMEWORK_DISPLAY_NAMES: { [key: string]: string } = {
  react_tailwind: 'React + TW',
  react_shadcn: 'React + ShadCN',
  react_chakra: 'React + Chakra UI',
  html_css: 'HTML + CSS',
  html_tailwind: 'HTML + TW',
  angular_css: 'Angular + CSS',
  vue_tailwind: 'Vue + TW',
  flutter: 'Flutter',
  reactnative: 'React Native',
};

interface RightTopBarProps {
  netlifyConfig: {
    NEXT_PUBLIC_NETLIFY_CLIENT_ID: string;
    NEXT_PUBLIC_NETLIFY_CLIENT_SECRET: string;
    NEXT_PUBLIC_NETLIFY_APP_URL: string;
  };
  isStreaming?: boolean;
  promptSubmited?: boolean;
}

const RightTopBar = ({ netlifyConfig, isStreaming = false, promptSubmited = false }: RightTopBarProps) => {
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isProUpgradeModalOpen, setIsProUpgradeModalOpen] = useState(false);
  const [techStack, setTechStack] = useState<{ framework: string; language: string } | null>(null);
  const [dbReady, setDbReady] = useState(false);

  const { email, isSubscribed } = userStore.get();

  const framework = selectionStore.get().framework;
  console.log('framework >>>>>>>>', framework);

  if (!framework) {
    return null;
  }

  // Determine if buttons should be disabled
  const isDeployDisabled =
    isStreaming || promptSubmited || DEPLOY_DISABLED_FRAMEWORKS.includes(framework.toLowerCase());

  const handleModalConfirm = () => {
    onDownloadClick();
    setIsDownloadModalOpen(false);
  };

  const confirmDownload = useCallback(() => {
    console.log('Download confirmed - starting download process');

    try {
      workbenchStore.downloadZip();
      console.log('Download initiated successfully');
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error during download',
          framework,
        },
      });
      console.error('Download failed:', error);
    }
    setIsDownloadModalOpen(false);
  }, []);

  const onDownloadClick = useCallback(() => {
    console.log('Download button clicked - opening modal');
    confirmDownload();
  }, []);

  useEffect(() => {
    // Check if IndexedDB is ready
    if (db) {
      setDbReady(true);
    }
  }, []);

  useEffect(() => {
    const getCurrentTechStack = async () => {
      const currentChatId = chatId.get();
      console.log('getCurrentTechStack called - chatId:', currentChatId);
      console.log('Current dbReady state:', dbReady);
      console.log('Current db instance:', db);
      if (!db || !currentChatId) {
        console.log('Exiting getCurrentTechStack - missing db or chatId');
        const framework = selectionStore.get().framework;
        const language = selectionStore.get().language;
        console.log('Setting techStack from selectionStore (no DB result):', { framework, language });
        setTechStack({ framework, language });
        return;
      }
      try {
        const result = await getFramework(db, currentChatId);
        console.log('Framework result from DB:', result);
        if (result) {
          const { framework, language } = result;
          console.log('Framework/language from DB:', { framework, language });
          if (framework && language && framework !== 'unknown' && language !== 'unknown') {
            console.log('Setting techStack from DB values:', { framework, language });
            setTechStack({ framework, language });
          } else {
            const framework = selectionStore.get().framework;
            const language = selectionStore.get().language;
            console.log('Setting techStack from selectionStore (DB values unknown):', { framework, language });
            setTechStack({ framework, language });
          }
        } else {
          const framework = selectionStore.get().framework;
          const language = selectionStore.get().language;
          console.log('Setting techStack from selectionStore (no DB result):', { framework, language });
          setTechStack({ framework, language });
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            message: 'Error getting framework',
          },
        });
        console.error('Error in getCurrentTechStack:', error);
        // Fallback to selection store
        const framework = selectionStore.get().framework;
        const language = selectionStore.get().language;
        console.log('Setting techStack from selectionStore (error fallback):', { framework, language });
        setTechStack({ framework, language });
      }
    };
    getCurrentTechStack();
  }, [dbReady, chatId.get()]);

  return (
    <>
      <div
        className="bg-alpha-background-editor-secondary rounded-t-2xl z-6"
        style={{
          width: 'calc(100% - 10px)',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '10px 20px 10px 10px',
          gap: '10px',
        }}
      >
        {techStack && (
          <div className="text-gray-200 flex flex-row gap-1 text-[9px]">
            <p className="flex items-center bg-alpha-background-editor-button rounded-md p-1">
              <span className="px-1">
                {techStack?.framework ? FRAMEWORK_DISPLAY_NAMES[techStack.framework] || techStack.framework : ''}
              </span>
            </p>
            <p className="flex items-center bg-alpha-background-editor-button rounded-md p-1">
              <span className="px-1">{techStack?.language?.toUpperCase()}</span>
            </p>
          </div>
        )}
        <div
          onClick={() => !isStreaming && setIsDownloadModalOpen(true)}
          className={`i-ph:download-simple-bold text-white ${
            isDeployDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          title={isDeployDisabled ? 'Download is disabled while streaming' : 'Download as zip'}
        ></div>
        <button
          className={`flex justify-center items-center px-3 py-1 rounded-md ${
            isDeployDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-94'
          } color-white`}
          style={{
            background: 'linear-gradient(rgb(48, 82, 255) 0%, rgb(21, 43, 150) 100%)',
          }}
          onClick={() => !isDeployDisabled && setIsDeployModalOpen(true)}
          disabled={isDeployDisabled}
          title={
            isStreaming
              ? 'Deploy is disabled while streaming'
              : isDeployDisabled
                ? 'Deploy is not available for this framework'
                : 'Deploy Instantly'
          }
        >
          <div className="flex items-center justify-center">Deploy</div>
        </button>
        {email && isSubscribed === false ? (
          <div
            className="cursor-pointer hover:bg-zinc-700 text-gray-200 px-4 py-2 rounded-full text-sm transition-colors duration-150 border-2 border-zinc-700/50 shadow-md whitespace-nowrap font-semibold"
            onClick={() => {
              setIsProUpgradeModalOpen(true);
            }}
          >
            Upgrade
          </div>
        ) : null}
        {/* <IssueDropdown framework={framework} /> */}
        <div className="z-200">
          <HelpMenu position="flex" framework={framework} withBg />
        </div>
      </div>

      <ProUpgradeModal
        isOpen={isProUpgradeModalOpen}
        onClose={() => setIsProUpgradeModalOpen(false)}
        description="Unlock unlimited messages per month and premium support"
      />

      <DeployModal
        isOpen={isDeployModalOpen}
        onClose={() => setIsDeployModalOpen(false)}
        netlifyConfig={netlifyConfig}
      />
      <DownloadModal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onConfirm={handleModalConfirm}
      />
    </>
  );
};

export default RightTopBar;
